import { Document } from '~publish/legacy/composer/composer/entities/Document'
import type { Draft } from '~publish/legacy/composer/composer/entities/Draft'

import { cloneOmniAsset } from '../postSlice/serialization'
import type { OmniAsset, OmniDraft } from '../postSlice/types'
import { draftMediaToAssets } from './mapDraftToOmni'

/**
 * The content fields kept in sync by the Flux→Zustand mirror. Excludes `id` and
 * `draftId`: `id` is fixed at `'omni'`, and `draftId` has no Flux omni-draft
 * counterpart.
 */
export type OmniContentProjection = Pick<
  OmniDraft,
  'text' | 'urls' | 'unshortenedUrls' | 'assets' | 'aiAssisted'
>

/**
 * Project a legacy Flux draft's content fields into the mirrored Zustand shape.
 * Every nested media object is deep-cloned so the projection shares no
 * references with the Flux draft: the Zustand store is immer-managed with
 * auto-freeze on, so writing shared media in would freeze the draft's live
 * objects and break in-place Flux edits (image alt text, user tags, thumbnails).
 *
 * Invariant: this function is pure — it reads the draft and returns a new
 * plain object on every call. Field-level identity is intentionally NOT
 * preserved (the mirror diffing layer handles that).
 */
export function projectFluxOmniContent(draft: Draft): OmniContentProjection {
  return {
    text: draft.text,
    urls: [...draft.urls],
    unshortenedUrls: [...draft.unshortenedUrls],
    assets: projectFluxOmniAssets(draft),
    aiAssisted: draft.aiAssisted,
  }
}

/**
 * The assets half of the projection, exported separately because it is the
 * expensive part: it deep-clones every attached media object. The mirror runs
 * it debounced (media changes are event-frequency) while the cheap text-family
 * fields sync on every Flux emission.
 *
 * Reads `draft.assets` directly rather than going through `draftMediaToAssets`
 * (which rebuilds canonical images→videos→document→gif order from the
 * per-kind fields): a mixed-media draft's assets can be manually interleaved
 * across kinds via `Draft.reorderAssetAt` (e.g. a video dragged between two
 * images), and `draft.assets` is the one view that preserves that order —
 * matching the save path's `mapMixedMediaAssets`, which iterates the same
 * array.
 */
export function projectFluxOmniAssets(draft: Draft): OmniAsset[] {
  if (draft.hasLinkAttachmentEnabled() && draft.link !== null) {
    return [cloneOmniAsset({ kind: 'link', link: draft.link })]
  }

  const assets: OmniAsset[] = []
  for (const asset of draft.assets) {
    switch (asset.kind) {
      case 'image':
        assets.push({ kind: 'image', image: asset.image })
        break
      case 'video':
        assets.push({ kind: 'video', video: asset.video })
        break
      case 'gif':
        assets.push({ kind: 'gif', gif: asset.gif })
        break
      case 'document':
        // An in-flight DocumentTitle placeholder has no url yet; skip it,
        // matching mapMixedMediaAssets.
        if (Document.isDocument(asset.document)) {
          assets.push({ kind: 'document', document: asset.document })
        }
        break
    }
  }
  return assets.map(cloneOmniAsset)
}

/**
 * Assets from every post in a thread, in thread order. The channel-less save
 * collapses a thread into one body, so it must keep media from non-root posts,
 * not only the root/active-post media that projectFluxOmniAssets returns. Link
 * attachments are dropped: a ContentItem draft has no link asset; the URL stays
 * inline in the joined text.
 */
export function projectFluxThreadAssets(draft: Draft): OmniAsset[] {
  if (!draft.hasThread()) return projectFluxOmniAssets(draft)
  return draft.thread.flatMap((post) =>
    draftMediaToAssets(
      {
        link: post.link,
        images: post.images,
        videos: post.videos,
        gif: post.gif,
      },
      { hasLinkAttachment: false },
    ).map(cloneOmniAsset),
  )
}
